import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Webcam from "react-webcam";
import { useDispatch } from "react-redux";
import useCamera from "../../../../hooks/useCamera";
import useScan from "../../../../hooks/useScan";
import { useStore } from "../../../../hooks/useStore";
import { useTranslation } from "../../../../hooks/useTranslation";
import { ISearchUser } from "../../../../store/types/userTypes";
import DialogConfirmation from "../Dialog/DialogConfirmation";
import NotCamera from "../../../Scan/NotCamera/NotCamera";
import SectionHeader from "../Header/SectionHeader";
import "./ScanDNI.css";
import DocumentCard from "../Cards/DocumentCard/DocumentCard";
import { startScanning, stopScanning } from "../../../../redux/actions/scanTesseractMrzActions";
import { selectCamera } from "../../../../redux/actions/types/cameraTypes";

type ScanDNIProps = {
  onScan?: (results: ISearchUser) => void;
  onManualSubmit?: (values: { country: string; dni: string }, userData?: any) => void;
  onBack?: () => void;
  mode?: "entry" | "exit";
  showCameraSelector?: boolean;
};

const ScanDNI: React.FC<ScanDNIProps> = ({
  onScan,
  onManualSubmit,
  onBack,
  mode,
  showCameraSelector,
}) => {
  const webcamRef = useRef<Webcam>(null);
  const { t, BundleKey } = useTranslation();
  const { getListOfCameras } = useCamera();
  const { cameraStore, mrzStore } = useStore();
  const dispatch = useDispatch();
  const {
    scanQrPdfCamera,
    resetScanQrPdfCamera,
    recognize,
    enableMrz,
    setEnableMrz,
    isMrz,
    result,
    error,
    reset,
  } = useScan(webcamRef);
  const [isResult, setIsResult] = useState(false);
  const [isWebcamReady, setIsWebcamReady] = useState(false);
  const [webcamError, setWebcamError] = useState<string | null>(null);

  // Refs to always hold the latest versions of unstable callbacks.
  // Assigning without useEffect keeps them in sync on every render without
  // adding them to effect dependency arrays, which would re-trigger camera
  // restart on every parent re-render (e.g. while the user types).
  const onScanRef = useRef(onScan);
  const scanQrPdfCameraRef = useRef(scanQrPdfCamera);
  const resetScanQrPdfCameraRef = useRef(resetScanQrPdfCamera);
  onScanRef.current = onScan;
  scanQrPdfCameraRef.current = scanQrPdfCamera;
  resetScanQrPdfCameraRef.current = resetScanQrPdfCamera;

  const stopWebcamTracks = () => {
    if (webcamRef.current) {
      if (webcamRef.current.stream) {
        webcamRef.current.stream.getTracks().forEach((track) => track.stop());
      }
      if (webcamRef.current.video?.srcObject) {
        try {
          const stream = webcamRef.current.video.srcObject as MediaStream;
          stream.getTracks().forEach((track) => track.stop());
        } catch (e) {
          console.error("Error stopping tracks on webcamRef:", e);
        }
        webcamRef.current.video.srcObject = null;
      }
    }
  };

  const handleBack = useCallback(() => {
    setIsResult(true);
    resetScanQrPdfCameraRef.current();
    stopWebcamTracks();
    dispatch(stopScanning());
    onBack?.();
  }, [dispatch, onBack]);

  useEffect(() => {
    if (result && !isResult) {
      setIsResult(true);
      resetScanQrPdfCameraRef.current();
      onScanRef.current?.(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, isResult]);

  useEffect(() => {
    getListOfCameras();
    return () => {
      setIsResult(true);
      resetScanQrPdfCameraRef.current();
      stopWebcamTracks();
      dispatch(stopScanning());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamRef]);

  const startCamera = useCallback(
    (deviceId: any) => {
      if (deviceId) {
        scanQrPdfCameraRef.current(webcamRef, deviceId);
      }
    },
    [webcamRef] // stable — uses ref so scanQrPdfCamera changes never re-trigger this
  );

  const handleClose = () => {
    setIsResult(false);
    startCamera(cameraStore.deviceId);
  };

  useEffect(() => {
    if (!isResult && cameraStore.deviceId) {
      startCamera(cameraStore.deviceId);
    }
  }, [cameraStore.deviceId, isResult, startCamera]);

  // MRZ scanning loop: trigger OCR recognize on each timer tick while scanning
  useEffect(() => {
    if (mrzStore.scanning) {
      recognize();
    }
  }, [mrzStore.timer, mrzStore.scanning, recognize]);

  // MRZ toggle: start/stop redux scanning state
  useEffect(() => {
    if (enableMrz) {
      dispatch(startScanning());
    } else {
      dispatch(stopScanning());
    }
  }, [enableMrz, dispatch]);

  useEffect(() => {
    // Limpieza: sin logs
  }, [error]);

  useEffect(() => {
    // Limpieza: sin logs
  }, [isWebcamReady, webcamError]);

  const videoConstraints = useMemo(
    () => ({
      deviceId: cameraStore.deviceId ? { exact: cameraStore.deviceId } : undefined,

      width: { ideal: 1920 },
      height: { ideal: 1080 },

      facingMode: "environment",

      aspectRatio: 1.777777778,

      frameRate: { ideal: 30, max: 60 },
    }),
    [cameraStore.deviceId]
  );

  if (cameraStore.empty) {
    return <NotCamera />;
  }

  return (
    <div className="scanDni-root">
      <SectionHeader title="Escanea el documento" className="titleHeader" onBack={handleBack} />

      {showCameraSelector !== false && cameraStore.cameras.length > 1 && (
        <div className="scanDni-camera-selector">
          <label className="scanDni-camera-label">Cámara</label>
          <select
            className="scanDni-camera-select"
            value={cameraStore.deviceId}
            onChange={(e) => {
              const device = cameraStore.cameras.find((c) => c.deviceId === e.target.value);
              if (device) dispatch(selectCamera(device));
            }}
          >
            {cameraStore.cameras.map((cam, i) => (
              <option key={cam.deviceId} value={cam.deviceId}>
                {cam.label || `Cámara ${i + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="scanDni-viewport">
        <Webcam
          audio={false}
          ref={webcamRef}
          mirrored={false}
          screenshotFormat="image/jpeg"
          onUserMedia={() => {
            setWebcamError(null);
            setIsWebcamReady(true);
            // Re-enumerate after permission is granted so mobile browsers
            // return all cameras with real labels and deviceIds.
            getListOfCameras();
            if (!isResult && cameraStore.deviceId) {
              scanQrPdfCameraRef.current(webcamRef, cameraStore.deviceId);
            }
          }}
          onUserMediaError={(err) => {
            setIsWebcamReady(false);
            setWebcamError(
              typeof err === "string" ? err : err.message || "No se pudo acceder a la camara"
            );
          }}
          videoConstraints={videoConstraints}
          className="scanDni-video"
        />

        {!isWebcamReady && !webcamError ? (
          <div className="scanDni-loading">Inicializando cámara...</div>
        ) : null}

        {isWebcamReady && !enableMrz && (
          <div className="scanDni-scan-overlay">
            <div className="scanDni-blur-band" />
            <div className="scanDni-scan-middle-row">
              <div className="scanDni-blur-side" />
              <div className="scanDni-scan-frame">
                <span className="scanDni-corner scanDni-corner--tl" />
                <span className="scanDni-corner scanDni-corner--tr" />
                <span className="scanDni-corner scanDni-corner--bl" />
                <span className="scanDni-corner scanDni-corner--br" />
              </div>
              <div className="scanDni-blur-side" />
            </div>
            <div className="scanDni-blur-band"></div>
          </div>
        )}

        {/* MRZ scanning overlay: shows a rectangle on the lower portion of the document */}
        {enableMrz && isWebcamReady && (
          <div className="scanDni-scan-overlay">
            <div className="scanDni-blur-band scanDni-mrz-top-band" />
            <div className="scanDni-scan-middle-row">
              <div className="scanDni-blur-side" />
              <div className={`scanDni-mrz-frame ${isMrz ? "scanDni-mrz-detected" : ""}`}>
                <span className="scanDni-corner scanDni-corner--tl" />
                <span className="scanDni-corner scanDni-corner--tr" />
                <span className="scanDni-corner scanDni-corner--bl" />
                <span className="scanDni-corner scanDni-corner--br" />
                <div className="scanDni-mrz-label">MRZ</div>
              </div>
              <div className="scanDni-blur-side" />
            </div>
            <div className="scanDni-blur-band" />
          </div>
        )}

        {/* MRZ toggle button */}
        {isWebcamReady && (
          <button
            onClick={() => setEnableMrz(!enableMrz)}
            className={`scanDni-mrz-toggle ${enableMrz ? "scanDni-mrz-toggle--active" : ""}`}
          >
            <span className="scanDni-mrz-toggle-icon">⎙</span>
            MRZ
          </button>
        )}

        {webcamError ? <div className="scanDni-error-overlay">{webcamError}</div> : null}
      </div>

      <DialogConfirmation
        open={error !== null}
        onClose={() => {
          reset();
          handleClose();
        }}
        onConfirm={() => {
          reset();
          handleClose();
        }}
        title={error ? error.title : ""}
        message={error?.message || ""}
        confirmLabel={t(BundleKey.ACTION_ACCEPT)}
        cancelLabel={t(BundleKey.ACTION_CANCEL)}
      />

      <div className="scanDni-document-card">
        <DocumentCard onSubmit={onManualSubmit} mode={mode} />
      </div>
    </div>
  );
};

export default React.memo(ScanDNI);
